/**
 * The by-coordinator table of the PAC daily report.
 *
 * Every column is a ratio rather than a bare count: 22 calls picked up is good
 * against 35 routed and poor against 60. Pure rendering — every figure comes off
 * the report object, so the rows cannot disagree with the hero tiles.
 */

import { PacCoordinatorRow, PacDailyReport, PacTeamTotals, Ratio, ratioPercent } from '@bariendo/lib/pac-daily-report';
import { ProviderAvailabilityStatus } from '@bariendo/lib/constants';
import { Badge, Group, Paper, Progress, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { ReactElement } from 'react';

export interface CoordinatorTableProps {
  report: PacDailyReport;
  /** Live status per coordinator. Absent when the day being viewed is not today. */
  availability: Record<string, ProviderAvailabilityStatus> | undefined;
}

/** An unknown percentage reads as an em dash, never `NaN%`. */
function formatPercent(ratio: Ratio): string {
  const percent = ratioPercent(ratio);
  return percent === undefined ? '—' : `${percent}%`;
}

/** `2h 14m`, or `48m` under the hour. Zero is a real answer and reads as `0m`. */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);

  // 59.6 minutes must not render as "1h 60m".
  if (minutes === 60) {
    return `${hours + 1}h`;
  }
  if (hours === 0) {
    return `${minutes}m`;
  }
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/**
 * A count over its denominator, with the percentage beside it.
 *
 * The raw pair stays visible rather than collapsing to a percentage: 1 of 2 and
 * 50 of 100 are both 50%, and only one of them is worth acting on.
 */
function RatioCell({ ratio }: { ratio: Ratio }): ReactElement {
  if (ratio.denominator === 0) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} justify="flex-end" wrap="nowrap">
      <Text size="sm">
        {ratio.numerator}/{ratio.denominator}
      </Text>
      <Text size="sm" c="dimmed">
        ({formatPercent(ratio)})
      </Text>
    </Group>
  );
}

/**
 * Talk time, and what share of the rostered shift it was.
 *
 * With no rostered window the bar is omitted entirely rather than drawn empty.
 * An unknown denominator and an idle shift are opposite readings, and a 0% bar
 * asserts the second when we only know the first — which is the state of every
 * coordinator today, because no Patient Access availability is maintained yet.
 */
function OccupancyCell({ row }: { row: PacCoordinatorRow }): ReactElement {
  const talkTime = formatDuration(row.talkTimeSeconds);

  if (!row.occupancy || row.occupancy.denominator === 0) {
    return (
      <Stack gap={2} align="flex-end">
        <Text size="sm">{talkTime} talk</Text>
        <Tooltip label="No availability is recorded for this coordinator today, so their shift length is unknown.">
          <Text size="xs" c="dimmed">
            occupancy —
          </Text>
        </Tooltip>
      </Stack>
    );
  }

  const percent = ratioPercent(row.occupancy) ?? 0;
  const shift = formatDuration(row.occupancy.denominator);

  return (
    <Stack gap={4} align="flex-end" style={{ minWidth: 140 }}>
      <Text size="sm">
        {talkTime} talk · {percent}% of a {shift} shift
      </Text>
      {/* Capped so an overrun renders as a full bar rather than overflowing;
          the figure beside it still reads over 100%. */}
      <Progress value={Math.min(percent, 100)} size="sm" w="100%" aria-label="Occupancy" />
    </Stack>
  );
}

/**
 * Online / on a call / do not disturb.
 *
 * The mockup shows an **Offline** state that the data cannot support: the
 * availability `Flag` has exactly two codes and no period, so nobody is ever
 * recorded as having left. Do-not-disturb is shown under its own name rather
 * than relabelled Offline — deliberately unavailable and not here are different
 * facts, and only one of them is knowable.
 *
 * On a call wins over both, because it is derived from the day's calls rather
 * than from a toggle someone may have forgotten.
 */
function StatusBadge({
  row,
  availability,
}: {
  row: PacCoordinatorRow;
  availability: Record<string, ProviderAvailabilityStatus> | undefined;
}): ReactElement | null {
  if (!availability) {
    return null;
  }

  if (row.hasLiveCall) {
    return (
      <Badge size="xs" variant="light" color="blue">
        On a call
      </Badge>
    );
  }

  // No Flag means online: that is what call routing believes about them, and
  // the dashboard should not disagree with the thing actually routing calls.
  const status = availability[row.reference] ?? ProviderAvailabilityStatus.ONLINE;

  return status === ProviderAvailabilityStatus.DO_NOT_DISTURB ? (
    <Badge size="xs" variant="light" color="red">
      Do not disturb
    </Badge>
  ) : (
    <Badge size="xs" variant="light" color="green">
      Online
    </Badge>
  );
}

function TeamTotalRow({ totals }: { totals: PacTeamTotals }): ReactElement {
  const percent = totals.occupancy ? ratioPercent(totals.occupancy) : undefined;

  return (
    <Table.Tr style={{ fontWeight: 600 }}>
      <Table.Td>Team total</Table.Td>
      <Table.Td align="right">
        <RatioCell ratio={totals.pickedUp} />
      </Table.Td>
      <Table.Td align="right">
        <RatioCell ratio={totals.introCalls} />
      </Table.Td>
      <Table.Td align="right">
        <RatioCell ratio={totals.consults} />
      </Table.Td>
      <Table.Td align="right">
        <Text size="sm">
          {formatDuration(totals.talkTimeSeconds)} talk
          {percent === undefined ? '' : ` · ${percent}%`}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}

export function CoordinatorTable({ report, availability }: CoordinatorTableProps): ReactElement {
  const { coordinators, teamTotal } = report;

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="baseline">
        <Title order={2} size="h4">
          By coordinator
        </Title>
        <Text size="sm" c="dimmed">
          {coordinators.length} on the roster
        </Text>
      </Group>

      <Paper withBorder radius="md">
        {coordinators.length === 0 ? (
          // Nobody holds the Patient Access role yet. An empty table with a header
          // row reads as a loading failure; this says what is actually true.
          <Text size="sm" c="dimmed" p="md">
            No Patient Access coordinators are on the roster.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Coordinator</Table.Th>
                  <Table.Th ta="right">Calls picked up</Table.Th>
                  <Table.Th ta="right">Scheduled intro call</Table.Th>
                  <Table.Th ta="right">Consults scheduled</Table.Th>
                  <Table.Th ta="right">Talk time</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {coordinators.map((row) => (
                  <Table.Tr key={row.reference}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm">{row.name}</Text>
                        <StatusBadge row={row} availability={availability} />
                      </Group>
                    </Table.Td>
                    <Table.Td align="right">
                      <RatioCell ratio={row.pickedUp} />
                    </Table.Td>
                    <Table.Td align="right">
                      <RatioCell ratio={row.introCalls} />
                    </Table.Td>
                    <Table.Td align="right">
                      <RatioCell ratio={row.consults} />
                    </Table.Td>
                    <Table.Td align="right">
                      <OccupancyCell row={row} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              <Table.Tfoot>
                <TeamTotalRow totals={teamTotal} />
              </Table.Tfoot>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>
    </Stack>
  );
}
